import { useEffect, useState, useMemo, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import {
  loadAllChannelsForAdmin,
  loadThread,
  loadAdminAccessLog,
  logAdminAccess,
  updateChannel,
  groupReactions,
  channelLabel,
} from '../../lib/messaging';

/**
 * Admin Safety Review.
 *
 * Default view loads channel metadata only — type, participants,
 * activity, flagged status. No message bodies are fetched until
 * the admin enters a reason and the access is logged.
 *
 * Once the reason is recorded, a read-only thread view opens with
 * a persistent banner showing who unlocked it, when, and why.
 * There is no composer, no edit, no delete affordance — the RLS
 * policies on messages would block those even if surfaced, but
 * the UI doesn't offer them either.
 */
export function SafetyReview() {
  const { profile } = useAuth();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [search, setSearch] = useState('');

  const [openChannelId, setOpenChannelId] = useState(null);
  const [pendingReason, setPendingReason] = useState(null); // channel obj awaiting reason
  const [reviewSession, setReviewSession] = useState(null); // { channelId, reason, accessedAt, thread }
  const [accessLog, setAccessLog] = useState([]);
  const [showMyHistory, setShowMyHistory] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const list = await loadAllChannelsForAdmin();
      setChannels(list);
    } catch (e) {
      console.warn('[safety] loadAllChannelsForAdmin error:', e?.message ?? e);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      await refresh();
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [refresh]);

  const visible = useMemo(() => {
    let list = channels;
    if (filterType !== 'all') list = list.filter(c => c.type === filterType);
    if (flaggedOnly) list = list.filter(c => c.flagged);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(c =>
        (c.name ?? '').toLowerCase().includes(s) ||
        c.participants.some(p => (p.full_name ?? '').toLowerCase().includes(s) || (p.email ?? '').toLowerCase().includes(s))
      );
    }
    return list;
  }, [channels, filterType, flaggedOnly, search]);

  async function startReview(channel) {
    setPendingReason(channel);
  }

  async function confirmReason(reason) {
    if (!pendingReason || !profile?.id) return;
    const ch = pendingReason;
    setPendingReason(null);
    try {
      const log = await logAdminAccess({
        adminId: profile.id,
        channelId: ch.id,
        reason,
      });
      // Pass userId as null so loadThread doesn't try to fetch a
      // myRole row for an admin who isn't a participant. Admins
      // pass RLS via is_app_admin().
      const thread = await loadThread(ch.id, null);
      setReviewSession({
        channelId: ch.id,
        reason,
        accessedAt: log.accessed_at,
        thread,
      });
      setOpenChannelId(ch.id);
    } catch (e) {
      alert(`Could not open: ${e?.message ?? e}`);
    }
  }

  async function toggleFlag(channelId, next) {
    try {
      await updateChannel(channelId, { flagged: next });
      await refresh();
      if (reviewSession?.channelId === channelId) {
        setReviewSession(s => ({
          ...s,
          thread: { ...s.thread, channel: { ...s.thread.channel, flagged: next } },
        }));
      }
    } catch (e) {
      alert(`Could not update flag: ${e?.message ?? e}`);
    }
  }

  async function loadMyHistory() {
    setShowMyHistory(true);
    try {
      const rows = await loadAdminAccessLog({ adminId: profile.id, limit: 50 });
      setAccessLog(rows);
    } catch (e) {
      console.warn('[safety] loadAdminAccessLog error:', e?.message ?? e);
    }
  }

  return (
    <Layout>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <p className="page-eyebrow">Admin · Safety</p>
          <h1 className="page-title">Safety Review</h1>
        </div>
        <button className="btn-xs btn-xs-ghost" onClick={loadMyHistory}>My access history</button>
      </div>

      <div style={{ background: '#fffbe6', border: '1px solid #fde68a', color: '#78350f', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
        <strong>Read-only review.</strong> Conversation contents are private by default. Opening a thread requires a written reason and is logged to the audit trail.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { key: 'all',          label: 'All' },
          { key: 'direct',       label: 'Direct' },
          { key: 'group',        label: 'Group' },
          { key: 'announcement', label: 'Announcement' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setFilterType(t.key)}
            style={{
              padding: '6px 12px', borderRadius: 999,
              border: '1px solid',
              borderColor: filterType === t.key ? 'var(--navy)' : 'var(--gray-200)',
              background: filterType === t.key ? 'var(--navy)' : '#fff',
              color: filterType === t.key ? '#fff' : 'var(--gray-700)',
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--gray-700)', marginLeft: 8 }}>
          <input
            type="checkbox"
            checked={flaggedOnly}
            onChange={e => setFlaggedOnly(e.target.checked)}
          />
          Flagged only
        </label>
        <input
          type="text"
          placeholder="Search participant or name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="form-input"
          style={{ flex: 1, minWidth: 220, maxWidth: 320, marginLeft: 'auto' }}
        />
      </div>

      <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '110px 1fr 90px 130px 110px 130px',
          padding: '10px 14px', background: 'var(--gray-50)',
          borderBottom: '1px solid var(--gray-200)',
          fontSize: 11, fontWeight: 800, color: 'var(--gray-500)',
          textTransform: 'uppercase', letterSpacing: 1.2,
        }}>
          <div>Type</div>
          <div>Channel / participants</div>
          <div>Messages</div>
          <div>Last activity</div>
          <div>Flagged</div>
          <div>Action</div>
        </div>
        {loading ? (
          <div style={{ padding: 20, color: 'var(--gray-500)', fontSize: 13 }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ padding: 20, color: 'var(--gray-500)', fontSize: 13 }}>No conversations match.</div>
        ) : visible.map(ch => (
          <div
            key={ch.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '110px 1fr 90px 130px 110px 130px',
              padding: '10px 14px',
              borderBottom: '1px solid var(--gray-100)',
              alignItems: 'center', gap: 8,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'capitalize' }}>{ch.type}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ch.name || (ch.type === 'direct'
                  ? ch.participants.map(p => p.full_name || p.email).join(' ↔ ')
                  : 'Unnamed')}
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ch.participants.length} · {ch.participants.map(p => p.full_name || p.email).slice(0, 4).join(', ')}{ch.participants.length > 4 ? '…' : ''}
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-700)' }}>{ch.message_count}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
              {ch.last_message_at ? new Date(ch.last_message_at).toLocaleString() : '—'}
            </div>
            <div>
              <button
                onClick={() => toggleFlag(ch.id, !ch.flagged)}
                title={ch.flagged ? 'Unflag' : 'Flag for follow-up'}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 18, padding: 0, opacity: ch.flagged ? 1 : 0.3,
                }}
              >🚩</button>
            </div>
            <div>
              <button
                className="btn-xs btn-xs-primary"
                onClick={() => startReview(ch)}
              >
                Open for review
              </button>
            </div>
          </div>
        ))}
      </div>

      {pendingReason && (
        <ReasonPromptModal
          channel={pendingReason}
          onCancel={() => setPendingReason(null)}
          onConfirm={confirmReason}
        />
      )}

      {reviewSession && openChannelId === reviewSession.channelId && (
        <ReviewThreadModal
          session={reviewSession}
          adminEmail={profile?.email}
          onClose={() => { setReviewSession(null); setOpenChannelId(null); }}
          onToggleFlag={next => toggleFlag(reviewSession.channelId, next)}
        />
      )}

      {showMyHistory && (
        <AccessHistoryModal
          rows={accessLog}
          channels={channels}
          onClose={() => setShowMyHistory(false)}
        />
      )}
    </Layout>
  );
}

// ── Reason gate ────────────────────────────────────────────────────

function ReasonPromptModal({ channel, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const ok = reason.trim().length >= 8;
  return (
    <ModalShell title="Why are you opening this conversation?" onClose={onCancel}>
      <p style={{ fontSize: 13, color: 'var(--gray-700)', marginBottom: 12, lineHeight: 1.5 }}>
        Your reason will be saved to the audit log along with your account, the time, and which conversation you opened. Be specific — this is what protects everyone.
      </p>
      <textarea
        value={reason}
        onChange={e => setReason(e.target.value)}
        rows={4}
        placeholder="e.g. Athlete reported concerning message in this channel; reviewing context."
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: 12, fontSize: 13, fontFamily: 'inherit',
          borderRadius: 8, border: '1.5px solid var(--gray-200)',
          outline: 'none', resize: 'vertical',
        }}
      />
      <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>
        {reason.trim().length}/8 minimum characters
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn-xs btn-xs-ghost" onClick={onCancel}>Cancel</button>
        <button
          className="btn btn-primary"
          onClick={() => ok && onConfirm(reason)}
          disabled={!ok}
          style={{ width: 'auto', padding: '8px 16px', opacity: ok ? 1 : 0.5 }}
        >
          Open conversation
        </button>
      </div>
    </ModalShell>
  );
}

// ── Read-only thread view ──────────────────────────────────────────

function ReviewThreadModal({ session, adminEmail, onClose, onToggleFlag }) {
  const { thread, reason, accessedAt } = session;
  const { channel, messages, reactions, profileById } = thread;
  const reactionsByMessage = useMemo(() => {
    const m = {};
    reactions.forEach(r => {
      if (!m[r.message_id]) m[r.message_id] = [];
      m[r.message_id].push(r);
    });
    return m;
  }, [reactions]);
  const title = channel.name || channelLabel({ ...channel, participants: thread.participants }, null);

  return (
    <ModalShell title={`Review: ${title}`} onClose={onClose} wide>
      <div style={{
        background: '#fffbe6', border: '1px solid #fde68a', color: '#78350f',
        borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12,
      }}>
        <div><strong>Read-only.</strong> Logged at {new Date(accessedAt).toLocaleString()} as {adminEmail}.</div>
        <div style={{ marginTop: 4 }}>Reason: {reason}</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
          {channel.type[0].toUpperCase() + channel.type.slice(1)} · {thread.participants.length} participants
        </div>
        <button
          onClick={() => onToggleFlag(!channel.flagged)}
          className="btn-xs btn-xs-ghost"
        >
          {channel.flagged ? '🚩 Unflag' : 'Flag for follow-up'}
        </button>
      </div>

      <div style={{
        border: '1px solid var(--gray-100)', borderRadius: 8,
        padding: 12, background: 'var(--gray-50)',
        maxHeight: '50vh', overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {messages.length === 0 ? (
          <div style={{ color: 'var(--gray-500)', fontSize: 13, textAlign: 'center', padding: 16 }}>
            No messages.
          </div>
        ) : messages.map(m => {
          const senderName = profileById[m.sender_id]?.full_name ?? profileById[m.sender_id]?.email ?? 'Unknown';
          const ts = new Date(m.created_at).toLocaleString();
          const groups = groupReactions(reactionsByMessage[m.id] ?? [], null);
          return (
            <div key={m.id} style={{ background: '#fff', borderRadius: 8, padding: 10, border: '1px solid var(--gray-100)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>
                <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{senderName}</span>
                <span>{ts}{m.edited_at ? ' · edited' : ''}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--gray-800)', whiteSpace: 'pre-wrap' }}>{m.content}</div>
              {groups.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {groups.map(g => (
                    <span key={g.emoji} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      padding: '2px 7px', borderRadius: 999,
                      border: '1px solid var(--gray-200)', background: '#fff',
                      fontSize: 11, fontWeight: 700, color: 'var(--gray-700)',
                    }}>
                      <span>{g.emoji}</span><span>{g.count}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="btn-xs btn-xs-ghost" onClick={onClose}>Close</button>
      </div>
    </ModalShell>
  );
}

// ── Personal access history ────────────────────────────────────────

function AccessHistoryModal({ rows, channels, onClose }) {
  const channelById = Object.fromEntries(channels.map(c => [c.id, c]));
  return (
    <ModalShell title="My access history" onClose={onClose} wide>
      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>No accesses recorded.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map(r => {
            const ch = channelById[r.channel_id];
            const label = ch ? (ch.name || `${ch.type} (${ch.participants.length})`) : r.channel_id;
            return (
              <div key={r.id} style={{
                padding: 10, border: '1px solid var(--gray-100)', borderRadius: 8,
                background: '#fff',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{label}</span>
                  <span style={{ color: 'var(--gray-500)' }}>{new Date(r.accessed_at).toLocaleString()}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-700)', marginTop: 4 }}>{r.reason}</div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button className="btn-xs btn-xs-ghost" onClick={onClose}>Close</button>
      </div>
    </ModalShell>
  );
}

// ── Modal shell ────────────────────────────────────────────────────

function ModalShell({ title, onClose, children, wide }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,24,44,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 10, padding: 20,
          width: '100%', maxWidth: wide ? 720 : 480, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--gray-400)', cursor: 'pointer', padding: 0 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
